import { useCallback, useEffect, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import {
	IconPen2Outline18,
	IconArrowUpFromLineOutline18 as UploadIcon,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import type { Membership, User } from "@productlane/zero"
import { starter } from "@productlane/lib/src/browser"
import {
	Form,
	FormInput,
	IconButton,
	Switch,
	Tooltip,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { Editor } from "@tiptap/core"
import { AvatarCropDialog } from "@/components/avatar-crop-dialog"
import { EmailEditor } from "@/components/editor/email-editor"
import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRequiredPlans } from "@/lib/plans"
import { useAutosaveText } from "@/lib/use-autosave-text"
import { mutateOk, useZero } from "@/lib/zero"
import { SettingsHeader } from "../settings-header"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import { ChangeEmailDialog } from "./change-email-dialog"
import { OutOfOfficeSettings } from "./out-of-office-settings"

const UpdateMembershipSchema = z.object({
	emailSignature: z.string().nullable().optional(),
})

function Signature({ membership }: { membership: Membership }) {
	const editorRef = useRef<{ getEditorInstance: () => Editor | null }>(null)
	const zero = useZero()

	const form = useZodForm({
		schema: UpdateMembershipSchema,
		defaultValues: {
			emailSignature: membership.emailSignature ?? "",
		},
	})

	const [emailSignature, setEmailSignature] = useState(
		membership.emailSignature ?? "",
	)
	const [isEdited, setIsEdited] = useState(false)

	const persist = async (value: string) => {
		try {
			await mutateOk(
				zero.mutate(
					mutators.memberships.update({
						id: membership.id,
						emailSignature: value,
					}),
				),
			)
			analytics.track("update-user-email-settings")
		} catch {
			toast.error({ title: "Failed to save signature" })
		}
	}

	// Only the user's own edits autosave, so a signature saved elsewhere is never
	// overwritten by the content this editor loaded with.
	useAutosaveText({
		value: emailSignature,
		persistedValue: membership.emailSignature ?? "",
		onSave: persist,
		enabled: isEdited,
	})

	return (
		<Form
			className="flex flex-col gap-2"
			form={form}
			onSubmit={() => undefined}
		>
			<div className="-mx-3.5 overflow-hidden rounded-[10px] bg-white shadow-card dark:bg-gray-850 dark:shadow-card-dark">
				<EmailEditor
					ref={editorRef}
					placeholder="Noah Ellis, Product Manager at Acme"
					initialContent={membership.emailSignature ?? undefined}
					keepRichPaste
					className="p-4"
					onUpdate={({ editor }) => {
						setEmailSignature(editor.getHTML())
						setIsEdited(true)
					}}
				/>
			</div>
		</Form>
	)
}

const ProfileSettingsFormSchema = z.object({
	imageUrlFile: z.instanceof(File).nullable(),
	imageUrl: z.string().nullable(),

	name: z.string().min(1, "Name must contain at least one character"),
})

export function ProfileSettingsPage() {
	const [user] = useQuery(queries.currentUser())

	if (!user) return null

	return (
		<>
			<MetaInfo title="Profile Settings" />

			<SettingsHeader
				title="Profile"
				subtitle="Change and review your account information."
			/>

			<ProfileSettingsForm data={user} membership={user.memberships ?? null} />
		</>
	)
}

interface GeneralFormProps {
	data: User
	membership: Membership | null
}

function ProfileSettingsForm({ data, membership }: GeneralFormProps) {
	const form = useZodForm({
		schema: ProfileSettingsFormSchema,
		defaultValues: {
			imageUrl: data.imageUrl,
			imageUrlFile: null,
			name: data.name ?? "",
		},
	})
	const zero = useZero()
	const { passes: hasSignaturePlan } = useRequiredPlans(starter.andUp)

	const imageInput = useRef<HTMLInputElement | null>(null)
	const openFilePicker = useCallback(() => {
		imageInput.current?.click()
	}, [imageInput])
	const [isChangingEmail, setIsChangingEmail] = useState(false)
	const navigate = useNavigate()
	const emailChanged = useSearch({
		strict: false,
		select: (s) => s.emailChanged,
	})

	useEffect(() => {
		if (emailChanged !== "1") return
		toast.success({ title: "Email changed" })
		void navigate({ to: ".", search: {}, replace: true })
	}, [emailChanged, navigate])

	const [imagePreview, setImagePreview] = useState<string | null>(null)
	const [cropSource, setCropSource] = useState<{
		url: string
		fileName: string
		fileType: string
	} | null>(null)
	const logoUrlWatch = form.watch("imageUrl")
	const nameWatch = form.watch("name")
	const getSignedUrlMutation = api.file.getUrl.useMutation()

	const persist = useCallback(
		async (values: z.infer<typeof ProfileSettingsFormSchema>) => {
			try {
				let imageUrl = values.imageUrl
				if (values.imageUrlFile) {
					const file = values.imageUrlFile
					const fileType = file.type
					const { fileUrl, uploadUrl } = await getSignedUrlMutation.mutateAsync(
						{
							fileName: file.name,
							fileType,
						},
					)
					const headers = { "Content-Type": fileType }
					const res = await fetch(uploadUrl, {
						method: "PUT",
						body: file,
						headers,
					})
					if (!res.ok) {
						throw new Error("File upload failed")
					}
					imageUrl = fileUrl
				}
				await mutateOk(
					zero.mutate(
						mutators.users.update({
							id: data.id,
							imageUrl: imageUrl ?? undefined,
							name: values.name,
						}),
					),
				)

				analytics.track("update-profile-settings")
				form.reset({
					imageUrlFile: null,
					imageUrl,
					name: values.name,
				})
			} catch {
				toast.error({ title: "Failed to save profile" })
			}
		},
		[data.id, form, getSignedUrlMutation, zero],
	)

	// An empty name fails the schema, so it never autosaves.
	useAutosaveText({
		value: nameWatch,
		persistedValue: data.name ?? "",
		onSave: (value) => persist({ ...form.getValues(), name: value }),
		enabled: !!form.formState.dirtyFields.name && nameWatch.trim().length > 0,
	})

	return (
		<>
			<Form
				className="flex flex-col gap-2"
				form={form}
				onSubmit={() => undefined}
			>
				<SettingsContainer className="mt-1">
					<SettingItem
						title="Profile Picture"
						description="Visible to teammates and customers. 256×256 px recommended."
						action={
							<>
								{!!imagePreview || !!logoUrlWatch ? (
									// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
									<div
										className="group relative h-12 w-12"
										onClick={openFilePicker}
									>
										<img
											alt="Profile"
											src={imagePreview ?? logoUrlWatch ?? ""}
											className="h-12 w-12 rounded-md border bg-gray-50 object-cover dark:bg-gray-850"
										/>
										<div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
											<UploadIcon className="h-4 w-4 text-white" />
										</div>
									</div>
								) : (
									// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
									<div
										className="flex h-12 w-12 items-center justify-center rounded-md bg-gray-100 hover:bg-gray-150 dark:bg-gray-800 dark:hover:bg-gray-750"
										onClick={openFilePicker}
									>
										<UploadIcon className="h-4 w-4 text-tertiary" />
									</div>
								)}
							</>
						}
					/>

					<SettingItem
						title="Email"
						className="pt-4"
						action={
							<div className="flex min-w-0 items-center gap-2">
								<span className="truncate text-sm text-primary">
									{data.email}
								</span>
								<Tooltip label="Change email">
									<IconButton
										Icon={IconPen2Outline18}
										variant="outline"
										aria-label="Change email"
										onClick={() => setIsChangingEmail(true)}
									/>
								</Tooltip>
							</div>
						}
					/>
					<SettingItem
						title="Full Name"
						className="pt-4"
						action={
							<FormInput
								name="name"
								autoComplete="name"
								placeholder="Full Name"
								className="max-w-52"
								onBlur={() => {
									if (
										form.formState.dirtyFields.name &&
										form.getValues("name")
									) {
										void persist(form.getValues())
									}
								}}
							/>
						}
					/>

					<ChangeEmailDialog
						isOpen={isChangingEmail}
						onOpenChange={setIsChangingEmail}
						currentEmail={data.email}
					/>
				</SettingsContainer>

				<OutOfOfficeSettings userId={data.id} membership={membership} />

				<div className="mt-8 text-sm font-medium">Replies</div>
				<SettingsContainer className="mt-1">
					<SettingItem
						title="Reply All by default"
						description="Start email replies with everyone on the thread in Cc."
						action={
							<Switch
								name="replyAllByDefault"
								checked={data.replyAllByDefault}
								onCheckedChange={async (value) => {
									await mutateOk(
										zero.mutate(
											mutators.users.update({
												id: data.id,
												replyAllByDefault: value,
											}),
										),
									)
									analytics.track("update-inbox-settings")
								}}
							/>
						}
					/>
				</SettingsContainer>
			</Form>

			<div className="mt-8 text-sm font-medium">Email signature</div>
			<div className="mt-3">
				{!hasSignaturePlan ? (
					<UpgradeButton required={starter.andUp} />
				) : membership ? (
					<Signature membership={membership} />
				) : null}
			</div>
			<input
				name="file"
				type="file"
				accept="image/*"
				ref={imageInput}
				className="hidden"
				onChange={(e) => {
					const file = e.target.files?.[0]
					if (file) {
						setCropSource({
							url: URL.createObjectURL(file),
							fileName: file.name,
							fileType: file.type,
						})
					}
					e.target.value = ""
				}}
			/>
			{cropSource ? (
				<AvatarCropDialog
					isOpen
					onOpenChange={(open) => {
						if (!open) {
							URL.revokeObjectURL(cropSource.url)
							setCropSource(null)
						}
					}}
					imageSrc={cropSource.url}
					fileName={cropSource.fileName}
					fileType={cropSource.fileType}
					onCropComplete={(croppedFile, previewUrl) => {
						setImagePreview(previewUrl)
						form.setValue("imageUrlFile", croppedFile, {
							shouldDirty: true,
						})
						void persist({ ...form.getValues(), imageUrlFile: croppedFile })
					}}
				/>
			) : null}
		</>
	)
}
